/**
 * Point d'entrée : connexion, chargement, câblage, temps réel.
 *
 * Ordre imposé par les dépendances — rien ne peut s'afficher avant de savoir
 * qui regarde (RLS refuserait les données), et la carte ne peut pas se dessiner
 * avant d'avoir les dossiers (le statut donne la couleur des marqueurs).
 */

// L'ORDRE DE CES DEUX IMPORTS EST PORTEUR DE SENS — ne pas les réordonner.
//
// Vite émet le CSS dans l'ordre d'évaluation des modules. Le CSS de MapLibre
// doit donc arriver en premier, sinon il écrase app.css à spécificité égale —
// c'est la dernière règle qui gagne. Symptôme observé : le curseur « main » de
// MapLibre revenait malgré le `cursor: default` d'app.css.
//
// Il est importé ici, dans le point d'entrée, et non dans map/index.js qui
// l'utilise : c'est le seul endroit d'où l'on maîtrise l'ordre.
import 'maplibre-gl/dist/maplibre-gl.css'
import './styles/app.css'

import { establishments } from './data/establishments.js'
import {
  addNote,
  getState,
  removeNote,
  removeRecord,
  setData,
  setEstablishments,
  setError,
  setRecord,
  setSettings,
  setUser,
  subscribe,
} from './state.js'
import { consumeAuthFragment, getSession, onAuthChange, signOut } from './supabase/auth.js'
import { configError, isConfigured } from './supabase/client.js'
import { fetchAll, subscribeToChanges } from './supabase/records.js'
import { guard, showConfigError } from './ui/auth-gate.js'
import { initDetail, updateDetail } from './ui/detail.js'
import { initList, renderList } from './ui/list.js'
import { initSidebar, updateSidebar } from './ui/sidebar.js'
import { toast } from './ui/toast.js'
import { createMap } from './map/index.js'

let started = false
let unsubscribeRealtime = null

async function start(session) {
  if (started) return
  started = true

  setUser(session.user)
  setEstablishments(establishments)

  try {
    setData(await fetchAll())
  } catch (err) {
    console.error(err)
    setError(err)
    toast(`Chargement impossible : ${err.message}`, { error: true, ms: 10_000 })
    return
  }

  const mapApi = createMap('map')
  const flyTo = (est, zoom) => mapApi.flyTo(est, zoom)

  initSidebar({ flyTo })
  initList({ flyTo })
  initDetail()

  subscribe((_state, changed) => {
    updateSidebar(changed)
    updateDetail(changed)
    if (['records', 'statusFilters', 'typeFilters', 'notes', 'listOpen'].some((c) => changed.includes(c))) {
      renderList(flyTo)
    }
  })

  // Les changements des collègues empruntent les mêmes mutations qu'un clic
  // local : mêmes vues redessinées, aucun chemin de rendu en double.
  unsubscribeRealtime = subscribeToChanges({
    onRecord: setRecord,
    onRecordDelete: removeRecord,
    onNoteInsert: addNote,
    onNoteDelete: removeNote,
    onSettings: setSettings,
  })
}

function stop() {
  unsubscribeRealtime?.()
  unsubscribeRealtime = null
  started = false
  // Rechargement plutôt que démontage manuel : la déconnexion est rare, et
  // défaire proprement la carte, les abonnements et l'état coûterait plus de
  // code que ça n'en vaut la peine — pour un risque de fuite bien réel.
  window.location.reload()
}

async function main() {
  if (!isConfigured) {
    showConfigError(configError)
    return
  }

  // getSession() d'abord : detectSessionInUrl a déjà lu le fragment au moment
  // où le client s'est construit, donc le nettoyer ici ne coupe rien. L'ordre
  // inverse, lui, effacerait le jeton avant que Supabase ne l'ait vu.
  const session = await getSession()
  const { error: authError } = consumeAuthFragment()

  // Une erreur de lien alors qu'une session valide existe déjà n'est pas un
  // échec : c'est un lien à usage unique rejoué (second clic, rechargement,
  // scanner de liens du fournisseur de messagerie). Rien à signaler.
  if (await guard(session, signOut, session ? null : authError)) await start(session)

  onAuthChange(async (next) => {
    if (next && !started) {
      if (await guard(next, signOut)) await start(next)
    } else if (!next && started) {
      stop()
    } else if (!next) {
      await guard(null, signOut)
    }
  })
}

main().catch((err) => {
  console.error(err)
  const state = getState()
  state.error = err
  toast(`Erreur au démarrage : ${err.message}`, { error: true, ms: 10_000 })
})
